import { useEffect, useState } from 'react';

import { Button, Dialog, Field, Input, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import { loiTheoTruong } from '../../../lib/errors';
import { useBoTieuChi, useTaoPhien } from '../hooks/useThamDinh';
import { ChonTepHoSo, DUOI_NHAN, TRAN_MOI_TEP } from './ChonTepHoSo';

export interface HopNopHoSoProps {
  open: boolean;
  onClose: () => void;
  /** Mở màn chi tiết sau khi nộp xong; để trống thì chỉ đóng hộp. */
  onXong?: (phienId: string) => void;
}

/**
 * Nộp hồ sơ để chấm (đặc tả mục 13.4).
 *
 * Chỉ liệt kê bộ tiêu chí **đã phát hành**: phiên ghim vào một bản bất biến (mục 2.4), nên bộ chưa phát hành
 * không chấm được — backend từ chối, và để nó trong ô chọn là mời người dùng vào một ngõ cụt.
 *
 * Nộp xong lâu hơn một POST thường vì backend trích văn bản ngay trong lời gọi; nút hiện trạng thái chạy.
 */
export function HopNopHoSo({ open, onClose, onXong }: HopNopHoSoProps) {
  const { t } = useT();
  const dichLoi = useLoi();
  const tao = useTaoPhien();

  const [boId, setBoId] = useState<string | undefined>();
  const [tieuDe, setTieuDe] = useState('');
  const [maThamChieu, setMaThamChieu] = useState('');
  const [tep, setTep] = useState<File[]>([]);

  // Chỉ bộ đang dùng; bộ lưu trữ không nhận hồ sơ mới.
  const ds = useBoTieuChi({ limit: 100, trang_thai: 'hoat_dong' });
  const boPhatHanh = (ds.data?.items ?? []).filter((b) => b.phien_ban_hien_tai !== null);

  useEffect(() => {
    if (!open) return;
    setBoId(undefined);
    setTieuDe('');
    setMaThamChieu('');
    setTep([]);
    tao.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loiTruong = loiTheoTruong(tao.error);
  const loiChung = tao.error && Object.keys(loiTruong).length === 0 ? dichLoi(tao.error) : undefined;
  const quaLon = tep.some((f) => f.size > TRAN_MOI_TEP);

  function nop() {
    if (!boId || !tieuDe.trim() || tep.length === 0) return;
    tao.mutate(
      { du: { bo_id: boId, tieu_de: tieuDe.trim(), ma_tham_chieu: maThamChieu.trim() }, tep },
      { onSuccess: (p) => { onClose(); onXong?.(p.id); } },
    );
  }

  return (
    <Dialog open={open} onClose={onClose} title={t('thamDinh.nop.tieuDe')}>
      <div className="space-y-4 pt-2">
        {loiChung && (
          <p className="rounded border border-danger-200 bg-danger-50 p-3 text-sm text-danger-700">{loiChung}</p>
        )}

        <Field
          label={t('thamDinh.nop.bo')}
          required
          hint={t('thamDinh.nop.boMo')}
          error={loiTruong['/bo_id']}
        >
          {(p) => (
            <Select
              {...p}
              className="w-full"
              value={boId}
              placeholder={t('thamDinh.nop.chonBo')}
              options={boPhatHanh.map((b) => ({ value: b.id, label: `${b.ten} (v${b.phien_ban_hien_tai})` }))}
              onChange={(v) => setBoId(v)}
            />
          )}
        </Field>

        {/* Chưa phát hành bộ nào thì nói thẳng việc cần làm, không để ô chọn rỗng không giải thích. */}
        {!ds.isPending && boPhatHanh.length === 0 && (
          <p className="rounded border border-warning-200 bg-warning-50 p-3 text-sm text-warning-700">
            {t('thamDinh.nop.chuaCoBo')}
          </p>
        )}

        <Field label={t('thamDinh.nop.tieuDeHoSo')} required error={loiTruong['/tieu_de']}>
          {(p) => <Input {...p} value={tieuDe} onChange={(e) => setTieuDe(e.target.value)} />}
        </Field>

        <Field
          label={t('thamDinh.nop.maThamChieu')}
          hint={t('thamDinh.nop.maThamChieuMo')}
          error={loiTruong['/ma_tham_chieu']}
        >
          {(p) => <Input {...p} value={maThamChieu} onChange={(e) => setMaThamChieu(e.target.value)} />}
        </Field>

        <Field label={t('thamDinh.nop.tep')} required hint={t('thamDinh.nop.tepMo', { duoi: DUOI_NHAN.join(', ') })}>
          {() => <ChonTepHoSo tep={tep} onDoi={setTep} />}
        </Field>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.huy')}
          </Button>
          <Button
            variant="primary"
            size="md"
            loading={tao.isPending}
            disabled={!boId || !tieuDe.trim() || tep.length === 0 || quaLon}
            onClick={nop}
          >
            {tao.isPending ? t('thamDinh.nop.dangNop') : t('thamDinh.nop.xacNhan')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
